import {
    Autocomplete, Badge, Button, Card, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import {
    IconGridDots,
    IconFilter,
    IconSortAscending2,
    IconSortDescending2,
} from "@tabler/icons-react";
import { getPager } from "helper/common";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import {
    clearFarmRecordList,
    closeFarmRecordsFilterDialog,
    getFarmRecordList,
    openFarmRecordsFilterDialog,
    setFarmRecordsFilteredData,
    farmRecordListErrorClear,
    farmRecordsExportErrorClear,
    getOverallSummary,
    getFarmRecordsExport
} from "store/app/farm-management/farm-records/actions";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import FilterFormRecords from "./filter";
import moment from "moment";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";
import { IconDownload } from "@tabler/icons-react";
import { excelSheetExport } from "helper/export";
import { NoSummary } from "./noSummary";

const tableHeaderStyle = { paddingLeft: '0px', textAlign: "left" };

const recordStatusOption = [
    { label: "Draft", value: "Draft" },
    { label: "Finalized", value: "Finalized" },
]

const TruncatedHeaderCell = ({ type, children }) => {
    const ref = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const check = () => {
            setShowTooltip(el.scrollWidth > el.clientWidth);
        };

        check();

        const observer = new ResizeObserver(check);
        observer.observe(el);

        return () => observer.disconnect();
    }, [children]);

    return (
        <TableCell sx={{ maxWidth: type === "header" ? 200 : 350 }}>
            <Tooltip title={showTooltip ? children : ''} arrow placement="top">
                <Typography
                    ref={ref}
                    variant={type === "header" ? "h5" : "inherit"}
                    noWrap
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: showTooltip ? "pointer" : ""
                    }}
                >
                    {children}
                </Typography>
            </Tooltip>
        </TableCell>
    );
};

const FarmRecordsList = (props) => {

    //--------------------List--------------------------------------
    const { farmRecordList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);
    const [extraHeaders, setExtraHeaders] = useState([]);

    //--------------------Filter------------------------------------
    const { isFarmRecordsFilterDialogOpen, farmRecordsFilterData } = props;

    const [isAutoOpenRecordStatus, setIsAutoOpenRecordStatus] = useState();
    const [filterCount, setFilterCount] = useState(0);
    const [isParamsApplied, setParamsApplied] = useState(false);
    const [searchParams] = useSearchParams();

    const filterObject = Object.fromEntries([...searchParams.entries()].map(([key, value]) => [
        key?.toLowerCase(),
        value?.toLowerCase(),
    ]));
    //--------------------Overall Summary---------------------------
    const { overallSummaryList, isOverallSummaryPending, overallSummaryErrorMessage } = props;
    //--------------------Export------------------------------------
    const { farmRecordsExportErrorMessage, isFarmRecordsExportPending } = props;

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    useEffect(() => {
        props.farmRecordListErrorClear();
        props.farmRecordsExportErrorClear();
        if (filterObject && Object.keys(filterObject).length > 0) {
            setParamsApplied(true);
            if (filterObject?.src === "dashboard") {
                delete filterObject.src;
                props.setFarmRecordsFilteredData({ ...filterObject, technicalmanageremail: filterObject?.technicalmanager })
            }
            else {
                props.setFarmRecordsFilteredData({ ...filterObject, ...farmRecordsFilterData })
            }
        }
        else {
            setParamsApplied(true);
            if (!farmRecordsFilterData?.recordstatus || !farmRecordsFilterData) {
                props.setFarmRecordsFilteredData({ ...farmRecordsFilterData, recordstatus: "finalized" })
            }
        }

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);

        return () => {
            props.clearFarmRecordList();
            window.removeEventListener("resize", handleResize);
            if (!farmRecordsFilterData?.recordstatus || !farmRecordsFilterData) {
                props.setFarmRecordsFilteredData({ ...farmRecordsFilterData, recordstatus: "finalized" })
            }
        }
    }, [])

    const handleSort = (column) => {
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    const setPage = (page = 1) => {
        props.getFarmRecordList(page, pageSize, sortOrder, sortDirection, farmRecordsFilterData);
        props.getOverallSummary(page, pageSize, sortOrder, sortDirection, farmRecordsFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    const getFormFieldsData = () => {
        if (farmRecordList) {
            farmRecordList?.map((element) => {
                const formFields = element?.formFieldAverageValues;
                const headers = extraHeaders?.length > 0 ? extraHeaders?.filter((title) => title !== undefined || title !== null || title !== "") : [];
                formFields?.map((field) => {
                    const isPresent = headers?.includes(field?.formFieldName);
                    if (!isPresent) {
                        headers?.push(field?.formFieldName);
                    }
                })
                setExtraHeaders(headers);
            })
        }
        else {
            setExtraHeaders([]);
        }
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager);
    }, [totalCounts]);

    useEffect(() => {
        getFormFieldsData();
    }, [farmRecordList]);

    useEffect(() => {
        if (isParamsApplied) {
            setPage();
        }
        if (farmRecordsFilterData) {
            const filterData = { ...farmRecordsFilterData };
            delete filterData.recordstatus;
            const count = filterData ? Object.keys(filterData).length : 0
            setFilterCount(count);
        }
    }, [isParamsApplied, farmRecordsFilterData, sortOrder, sortDirection, pageSize]);

    const handleSelect = (e, option) => {
        const { label } = option || {};

        if (label) {
            props.setFarmRecordsFilteredData({
                ...farmRecordsFilterData,
                recordstatus: label,
            })
        }
        else {
            const filter = { ...farmRecordsFilterData };
            if (filter) {
                if (Object.hasOwn(filter, "recordstatus")) {
                    delete filter.recordstatus;
                }
            }
            const removedRecordStatus = filter ? Object.keys(filter).length === 0 ? null : filter : null
            props.setFarmRecordsFilteredData(removedRecordStatus)
        }
    }

    const handleFilterOpen = () => {
        props.openFarmRecordsFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeFarmRecordsFilterDialog();
    }

    //--------------------EXPORT---------------------------------
    const handleExportFormRecords = (e) => {
        e.preventDefault();
        const page = 1;
        props.getFarmRecordsExport(page, totalCounts, sortOrder, sortDirection, farmRecordsFilterData)
            .then((records) => {
                if (records) {
                    const { farmRecords } = records || [];
                    const exportFormRecordsLists = [];
                    const headerExcel = [
                        { headerName: "Record No.", key: "recordsIndex", headerWidhth: 20 },
                        { headerName: "Record Status", key: "recordStatus", headerWidhth: 20 },
                        { headerName: "Farm Name", key: "farmName", headerWidhth: 30 },
                        { headerName: "Technical Manager Name", key: "technicalManagerUserName", headerWidhth: 30 },
                        { headerName: "Technical Manager Email", key: "technicalManagerUserEmail", headerWidhth: 50 },
                        { headerName: "Customer Name", key: "customerName", headerWidhth: 30 },
                        { headerName: "Customer Email", key: "customerEmail", headerWidhth: 50 },
                        { headerName: "Inspection Date", key: "inspectionDate", headerWidhth: 30 },
                        { headerName: "Location", key: "location", headerWidhth: 30 },
                        { headerName: "Region Name", key: "regionName", headerWidhth: 30 },
                        { headerName: "State/Export", key: "state", headerWidhth: 30 },
                        { headerName: "District", key: "district", headerWidhth: 30 },
                        { headerName: "Age", key: "age", headerWidhth: 10 },
                        { headerName: "Gut & Organ Images Count", key: "totalBirdImageCount", headerWidhth: 40 },
                        { headerName: "Examined Birds Count ", key: "examinedBirdsCount", headerWidhth: 30 },
                        { headerName: "Total No Of Birds Placed", key: "totalNoOfBirdsPlaced", headerWidhth: 40 },
                        { headerName: "Anticoccidial", key: "anticoccidial", headerWidhth: 30 },
                        { headerName: "AGP", key: "agp", headerWidhth: 30 },
                        { headerName: "Probiotic", key: "probiotic", headerWidhth: 30 },
                        { headerName: "Remark", key: "remark", headerWidhth: 50 }
                    ]
                    farmRecords && farmRecords?.forEach((element) => {
                        const formFields = element?.formFieldAverageValues || [];
                        const fieldNameValueArray = formFields?.map((field) => [field?.formFieldName, field?.formFieldAverage]);
                        const formFieldsObject = Object.fromEntries(fieldNameValueArray);
                        formFields?.map((field) => {
                            const isPresent = headerExcel?.some((header) => header?.headerName === field?.formFieldName);
                            if (!isPresent) {
                                headerExcel?.push({ headerName: field?.formFieldName, key: field?.formFieldName })
                            }
                        })
                        exportFormRecordsLists?.push({
                            ...formFieldsObject,
                            recordsIndex: element.recordsIndex || "N/A",
                            recordStatus: element.recordStatus || "N/A",
                            farmName: element.farmName || "N/A",
                            technicalManagerUserName: element.technicalManagerUserName || "N/A",
                            technicalManagerUserEmail: element.technicalManagerUserEmail || "N/A",
                            customerName: element.customerName || "N/A",
                            customerEmail: element.customerEmail || "N/A",
                            inspectionDate: element.inspectionDate ? moment(element.inspectionDate).format('DD MMM YYYY') : "N/A",
                            submittedTimeStamp: element.submittedTimeStamp ? moment(element.submittedTimeStamp).format('DD MMM YYYY') : "N/A",
                            location: element.location || "N/A",
                            regionName: element.regionName || "N/A",
                            state: element.stateName || "N/A",
                            district: element.districtName || "N/A",
                            age: element.age || "N/A",
                            totalBirdImageCount: element.totalBirdImageCount || 0,
                            examinedBirdsCount: element.examinedBirdsCount || 0,
                            totalNoOfBirdsPlaced: element.totalNoOfBirdsPlaced || 0,
                            anticoccidial: element.anticoccidial || "N/A",
                            agp: element.agp || "N/A",
                            probiotic: element.probiotic || "N/A",
                            remark: element.remark || "N/A",
                        });
                    });
                    excelSheetExport(headerExcel, exportFormRecordsLists, "Farm Records List");
                }
            })
    }

    return (
        <>
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Farm Records List - </Typography>
                    <Tooltip title="Form Record Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: "150px" }}
                        disablePortal
                        options={recordStatusOption}
                        open={isAutoOpenRecordStatus}
                        onOpen={() => setIsAutoOpenRecordStatus(true)}
                        onClose={() => setIsAutoOpenRecordStatus(false)}
                        onChange={(e, option) => handleSelect(e, option)}
                        value={farmRecordsFilterData?.recordstatus || ""}
                        renderInput={(params) =>
                            <TextField
                                {...params}
                                label="Record Status"
                            />
                        }
                    />
                    <Badge
                        badgeContent={filterCount}
                        color="secondary"
                        invisible={filterCount === 0}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {totalCounts > 0 &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleExportFormRecords}
                            startIcon={!isFarmRecordsExportPending && <IconDownload />}
                            disabled={isFarmRecordsExportPending}
                        >
                            {isFarmRecordsExportPending ?
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <CircularProgress size={'20px'} color='inherit' />
                                    Loading...
                                </div>
                                : <>Export</>
                            }
                        </Button>
                    }
                </Grid>
            </SubCard>
            <MainCard sx={{ marginTop: '15px' }}>
                <Typography variant="h4">
                    Total Summary
                </Typography>
                {isOverallSummaryPending ?
                    <Grid container alignItems={"center"} justifyContent={"center"} gap={1}>
                        <CircularProgress />
                        <Typography variant="h5" color={'primary'}> Loading...</Typography>
                    </Grid>
                    : overallSummaryErrorMessage ?
                        <Grid container direction="column" marginTop={2}>
                            <ErrorAlert recordStatusFilter={'error'} content={overallSummaryErrorMessage} />
                            <NoSummary />
                        </Grid>
                        : <Grid container marginTop={2} columnSpacing="8" rowSpacing="8" justifyContent="space-between">
                            {overallSummaryList?.length > 0 ?
                                overallSummaryList?.map((summary, idx) => (
                                    <Grid item xl="2" lg="3" md="4" sm="6" xs="12" key={idx}>
                                        <Card
                                            sx={{
                                                bgcolor: theme.palette.secondary.light,
                                                color: theme.palette.primary.main,
                                                fontWeight: "500",
                                                padding: "10px",
                                            }}
                                        >
                                            <Grid container direction="column" justifyContent="center" alignItems="center" rowSpacing="4">
                                                <Grid item sx={{ fontSize: "18px", fontWeight: "600" }}>
                                                    {summary.reportValue}
                                                </Grid>
                                                <Grid item sx={{
                                                    fontSize: "14px",
                                                    cursor: "pointer",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: '100%',
                                                    textAlign: "center"
                                                }}>
                                                    <Tooltip title={summary.reportName} placement="bottom" arrow>
                                                        {summary.reportName}
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    </Grid>))
                                : <NoSummary />
                            }
                        </Grid>}
            </MainCard>
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert recordStatusFilter={'error'} content={errorMessage} />}
                {farmRecordsExportErrorMessage && <ErrorAlert recordStatusFilter={'error'} content={farmRecordsExportErrorMessage} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 450px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('recordsIndex')}
                                        endIcon={sortOrder === 'recordsIndex' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Record No.
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('recordStatus')}
                                        endIcon={sortOrder === 'recordStatus' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Record Status
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('farmName')}
                                        endIcon={sortOrder === 'farmName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Farm Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('technicalManagerUserName')}
                                        endIcon={sortOrder === 'technicalManagerUserName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Technical Manager
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('customerName')}
                                        endIcon={sortOrder === 'customerName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Customer
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('inspectionDate')}
                                        endIcon={sortOrder === 'inspectionDate' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Inspection Date
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('submittedTimeStamp')}
                                        endIcon={sortOrder === 'submittedTimeStamp' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Submitted On
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('location')}
                                        endIcon={sortOrder === 'location' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Location
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('regionName')}
                                        endIcon={sortOrder === 'regionName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Region Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('stateName')}
                                        endIcon={sortOrder === 'stateName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        State/Export
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('districtName')}
                                        endIcon={sortOrder === 'districtName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        District
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('age')}
                                        endIcon={sortOrder === 'age' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Age &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('totalBirdImageCount')}
                                        endIcon={sortOrder === 'totalBirdImageCount' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Gut & Organ Images Count &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('examinedBirdsCount')}
                                        endIcon={sortOrder === 'examinedBirdsCount' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Examined  Birds Count
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('totalNoOfBirdsPlaced')}
                                        endIcon={sortOrder === 'totalNoOfBirdsPlaced' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Total No Of Birds Placed
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('anticoccidial')}
                                        endIcon={sortOrder === 'anticoccidial' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Anticoccidial
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('agp')}
                                        endIcon={sortOrder === 'agp' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        AGP &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('probiotic')}
                                        endIcon={sortOrder === 'probiotic' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Probiotic
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('remark')}
                                        endIcon={sortOrder === 'remark' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Remark
                                    </Button>
                                </TableCell>
                                {extraHeaders?.map((extraHeaders, idx) => (
                                    <TruncatedHeaderCell key={idx} type="header">{extraHeaders}</TruncatedHeaderCell>
                                ))}
                                <TableCell>View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(farmRecordList?.length > 0) && !isPending ?
                                farmRecordList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{data.recordsIndex || 'N/A'}</TableCell>
                                        <TableCell>{data.recordStatus || 'N/A'}</TableCell>
                                        <TableCell>{data.farmName || 'N/A'}</TableCell>
                                        <TableCell>
                                            {data.technicalManagerUserName || 'N/A'}<br />
                                            <small> {data.technicalManagerUserEmail || 'N/A'}</small>
                                        </TableCell>
                                        <TableCell>
                                            {data.customerName || 'N/A'}<br />
                                            <small>{data.customerEmail || 'N/A'}</small>
                                        </TableCell>
                                        <TableCell>{data.inspectionDate ? moment(data.inspectionDate).format("DD MMM YYYY") : 'N/A'}</TableCell>
                                        <TableCell>{data.submittedTimeStamp ? moment(data.submittedTimeStamp).format("DD MMM YYYY") : 'N/A'}</TableCell>
                                        <TruncatedHeaderCell type="body">{data.location || 'N/A'}</TruncatedHeaderCell>
                                        <TableCell>{data.regionName || 'N/A'}</TableCell>
                                        <TableCell>{data.stateName || 'N/A'}</TableCell>
                                        <TableCell>{data.districtName || 'N/A'}</TableCell>
                                        <TableCell>{data.age || 'N/A'}</TableCell>
                                        <TableCell>{data.totalBirdImageCount ? data.totalBirdImageCount : data.totalBirdImageCount === 0 ? 0 : 'N/A'}</TableCell>
                                        <TableCell>{data.examinedBirdsCount ? data.examinedBirdsCount : data.examinedBirdsCount === 0 ? 0 : 'N/A'}</TableCell>
                                        <TableCell>{data.totalNoOfBirdsPlaced ? data.totalNoOfBirdsPlaced : data.totalNoOfBirdsPlaced === 0 ? 0 : 'N/A'}</TableCell>
                                        <TableCell>{data.anticoccidial || 'N/A'}</TableCell>
                                        <TableCell>{data.agp || 'N/A'}</TableCell>
                                        <TableCell>{data.probiotic || 'N/A'}</TableCell>
                                        <TruncatedHeaderCell type="body">{data.remark || 'N/A'}</TruncatedHeaderCell>
                                        {extraHeaders?.map((extraHeaders, idx) => {
                                            const selectedObj = data?.formFieldAverageValues?.find((field) => field?.formFieldName === extraHeaders)
                                            return (
                                                < TableCell key={idx} >
                                                    {selectedObj?.formFieldAverage || 0}
                                                </TableCell>
                                            )
                                        })}
                                        <TableCell>
                                            <Link to={`/farm-records/details/${data.id}`}>
                                                <IconGridDots color={theme.palette.primary.main} />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={19} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    : <TableCell colSpan={19} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {(totalCounts > 0) &&
                    <Grid>
                        <VWPagination
                            pageDetails={Pager}
                            pageChange={setPage}
                            totalRecords={totalCounts}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    </Grid>
                }
            </MainCard >
            <br />
            <FilterFormRecords
                open={isFarmRecordsFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    farmRecordList: state.farmRecords.farmRecordList,
    totalCounts: state.farmRecords.farmRecordListCount,
    isPending: state.farmRecords.isFarmRecordListPending,
    errorMessage: state.farmRecords.farmRecordListErrorMessage,
    //--------------------Overall Summary---------------------------
    overallSummaryList: state.farmRecords.overallSummaryList,
    isOverallSummaryPending: state.farmRecords.isOverallSummaryPending,
    overallSummaryErrorMessage: state.farmRecords.overallSummaryErrorMessage,
    //--------------------Filter------------------------------------
    isFarmRecordsFilterDialogOpen: state.farmRecords.isFarmRecordsFilterDialogOpen,
    farmRecordsFilterData: state.farmRecords.farmRecordsFilterData,
    //--------------------Export------------------------------------
    isFarmRecordsExportPending: state.farmRecords.isFarmRecordsExportPending,
    farmRecordsExportErrorMessage: state.farmRecords.farmRecordsExportErrorMessage,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getFarmRecordList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getFarmRecordList(page, pageSize, sortOrder, sortDirection, filter)),
    clearFarmRecordList: () => dispatch(clearFarmRecordList()),
    farmRecordListErrorClear: () => dispatch(farmRecordListErrorClear()),
    //--------------------Overall Summary---------------------------
    getOverallSummary: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getOverallSummary(page, pageSize, sortOrder, sortDirection, filter)),
    //--------------------Filter------------------------------------
    openFarmRecordsFilterDialog: () => dispatch(openFarmRecordsFilterDialog()),
    closeFarmRecordsFilterDialog: () => dispatch(closeFarmRecordsFilterDialog()),
    setFarmRecordsFilteredData: (filter) => dispatch(setFarmRecordsFilteredData(filter)),
    //--------------------Export------------------------------------
    getFarmRecordsExport: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getFarmRecordsExport(page, pageSize, sortOrder, sortDirection, filter)),
    farmRecordsExportErrorClear: () => dispatch(farmRecordsExportErrorClear()),
})
export default connect(mapStateToProps, mapDispatchToProps)(FarmRecordsList);